/**
 * Insert-flow — click-drag and right-click overlay insertion.
 *
 *  • Click+drag: creates a new overlay box (type UNDEFINED — dashed grey + ? badge).
 *  • Right-click: "Insert new element" menu, default ~1/3 × 1/10 page size.
 *  • Auto-snap: rubber-band edges snap to detectable PDF elements within ~6px.
 *  • New card appears in the cards pane at the closest-sibling-above insertion point.
 *  • Save is blocked while ANY UNDEFINED element exists.
 *
 * Pure geometry helpers live in insert-geometry.ts; re-exported here for
 * backwards compatibility — tests import from `./insert-flow.js`.
 *
 * Public API (controller + re-exports):
 *   InsertFlowController                — factory; wires pointer events on a per-page overlay.
 *   saveBlockState(store)               — save-block predicate.
 *   resolvePendingType(store, id, role) — type-resolution op builder.
 *   snapEdgesToBoxes / defaultInsertSize / findClosestSiblingAbove / nextEditorInsertId
 *                                       — re-exported from insert-geometry.ts.
 *
 * @module insert-flow
 */
import { GestureController } from "./gesture-controller.js";
import { snapEdgesToBoxes, nextEditorInsertId, defaultInsertSize, findClosestSiblingAbove, } from "./insert-geometry.js";
export { snapEdgesToBoxes, defaultInsertSize, findClosestSiblingAbove, nextEditorInsertId, } from "./insert-geometry.js";
// ─── InsertFlowController — pointer wiring ────────────────────────────────────
const SNAP_TOLERANCE_PX = 6;
const MIN_INSERT_PX = 10;
/**
 * Wire click+drag and right-click insertion on a per-page overlay element.
 */
export function InsertFlowController(hooks) {
    if (!hooks || !hooks.overlay || typeof hooks.onInsert !== "function") {
        throw new Error("InsertFlowController: overlay + onInsert required");
    }
    const overlay = hooks.overlay;
    const doc = overlay.ownerDocument || (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("InsertFlowController: no document");
    let ctxMenuEl = null;
    /**
     * Click-drag rubber-band gesture. Engagement requires:
     *  • Left button.
     *  • No shift/cmd/ctrl modifier (those are reserved for rubber-band-select).
     *  • Pointer on the empty overlay (not on an existing bbox or resize handle).
     *
     * Side effects:
     *   on engage:  creates an .editor-insert-marquee div in the overlay.
     *   on move:    updates marquee position + per-edge snap classes.
     *   on commit:  removes marquee; fires hooks.onInsert if rect ≥ MIN_INSERT_PX.
     *   on cancel:  removes marquee; no op fired.
     */
    class InsertGesture extends GestureController {
        onPointerDown(ev) {
            // Only left-button initiates drag. Right-click handled separately.
            if (ev.button !== 0)
                return null;
            // shift-drag is the rubber-band multi-select gesture (rubber-band.js)
            // — we yield to it. Cmd/Ctrl-drag has no defined gesture today, but
            // for parity reserve it for future use (toggle-multi-select).
            if (ev.shiftKey || ev.metaKey || ev.ctrlKey)
                return null;
            // Only fire when the click target is the overlay itself or empty —
            // not when it lands on an existing bbox div.
            const tgt = ev.target;
            if (tgt !== overlay && tgt?.classList?.contains("editor-overlay-bbox"))
                return null;
            if (tgt !== overlay && tgt?.closest?.(".editor-overlay-bbox"))
                return null;
            // Resize handles are also off-limits for the create-new gesture.
            if (tgt !== overlay && tgt?.closest?.(".editor-resize-handle"))
                return null;
            const rect = overlay.getBoundingClientRect();
            const dragStart = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
            if (typeof ev.preventDefault === "function")
                ev.preventDefault();
            const marqueeEl = doc.createElement("div");
            marqueeEl.className = "editor-insert-marquee";
            marqueeEl.setAttribute("aria-hidden", "true");
            overlay.appendChild(marqueeEl);
            return { dragStart, lastSnap: null, marqueeEl };
        }
        onPointerMove(context, ev) {
            const rect = overlay.getBoundingClientRect();
            const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
            const raw = {
                x: Math.min(context.dragStart.x, cur.x),
                y: Math.min(context.dragStart.y, cur.y),
                w: Math.abs(cur.x - context.dragStart.x),
                h: Math.abs(cur.y - context.dragStart.y),
            };
            const boxes = (typeof hooks.getExistingBoxes === "function") ? hooks.getExistingBoxes() : [];
            context.lastSnap = snapEdgesToBoxes(raw, boxes, SNAP_TOLERANCE_PX);
            const m = context.marqueeEl;
            if (m) {
                m.style.left = `${context.lastSnap.x}px`;
                m.style.top = `${context.lastSnap.y}px`;
                m.style.width = `${context.lastSnap.w}px`;
                m.style.height = `${context.lastSnap.h}px`;
                const anySnap = context.lastSnap.snapped.left || context.lastSnap.snapped.right
                    || context.lastSnap.snapped.top || context.lastSnap.snapped.bottom;
                m.classList.toggle("is-snapped", anySnap);
                // Per-side magnetize cue: snapped sides light up green;
                // un-snapped sides keep the dashed-yellow free-form style.
                m.classList.toggle("is-snapped-l", !!context.lastSnap.snapped.left);
                m.classList.toggle("is-snapped-r", !!context.lastSnap.snapped.right);
                m.classList.toggle("is-snapped-t", !!context.lastSnap.snapped.top);
                m.classList.toggle("is-snapped-b", !!context.lastSnap.snapped.bottom);
            }
        }
        onCommit(context) {
            const r = context.lastSnap ?? {
                x: 0, y: 0, w: 0, h: 0,
                snapped: { left: false, right: false, top: false, bottom: false },
            };
            removeMarquee(context.marqueeEl);
            if (r.w < MIN_INSERT_PX || r.h < MIN_INSERT_PX) {
                // Below the min size — treat as a click, not a drag.
                return;
            }
            // Bug 2: when all four edges of the rubber-band snapped to
            // existing boxes, the resulting region's geometry is fully
            // derived from existing structure — resize handles on the
            // overlay would let the user fight the snap, which is the
            // wrong affordance. Surface the snap state to the host so
            // the rendered overlay can suppress handles when locked.
            const allSnapped = !!(r.snapped
                && r.snapped.left && r.snapped.right
                && r.snapped.top && r.snapped.bottom);
            commitInsert(r, { allSnapped });
        }
        onCancel(context) {
            removeMarquee(context.marqueeEl);
        }
    }
    function removeMarquee(el) {
        if (el && el.parentNode) {
            try {
                el.parentNode.removeChild(el);
            }
            catch (_) { /* element already detached during teardown; harmless */ }
        }
    }
    function commitInsert(canvasRect, opts) {
        const editorId = nextEditorInsertId();
        const pdfBbox = (typeof hooks.toPdfBbox === "function")
            ? hooks.toPdfBbox(canvasRect)
            : null;
        let anchorStructId = null;
        if (typeof hooks.getCardsForAnchor === "function") {
            const cards = hooks.getCardsForAnchor() ?? [];
            const anchor = findClosestSiblingAbove(canvasRect, cards, hooks.page);
            anchorStructId = anchor ? anchor.structId : null;
        }
        hooks.onInsert({
            editorId,
            page: hooks.page,
            bbox: canvasRect,
            pdfBbox: pdfBbox ?? null,
            anchorStructId,
            // Snapped-on-all-sides flag: the host stores it on the pending-
            // insert entry and forwards it onto the rendered overlay so
            // resize handles can be suppressed (bug 2).
            snappedAllSides: !!(opts && opts.allSnapped),
        });
    }
    function onContextMenu(ev) {
        // Only on empty PDF area (not on existing overlays).
        const tgt = ev.target;
        if (tgt !== overlay && tgt?.closest?.(".editor-overlay-bbox"))
            return;
        ev.preventDefault();
        const rect = overlay.getBoundingClientRect();
        const clickPt = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
        showContextMenu(clickPt, ev.clientX, ev.clientY);
    }
    function showContextMenu(clickPt, screenX, screenY) {
        hideContextMenu();
        ctxMenuEl = doc.createElement("div");
        ctxMenuEl.className = "editor-insert-ctxmenu";
        ctxMenuEl.setAttribute("role", "menu");
        const item = doc.createElement("button");
        item.className = "editor-insert-ctxmenu-item";
        item.setAttribute("role", "menuitem");
        item.textContent = "Insert new element";
        item.addEventListener("click", (ev2) => {
            ev2.stopPropagation();
            hideContextMenu();
            const page = hooks.getPageSize();
            const r = defaultInsertSize({ width: page.width, height: page.height }, clickPt);
            commitInsert(r);
        });
        ctxMenuEl.appendChild(item);
        ctxMenuEl.style.position = "fixed";
        ctxMenuEl.style.left = `${screenX}px`;
        ctxMenuEl.style.top = `${screenY}px`;
        (doc.body || overlay).appendChild(ctxMenuEl);
        // Dismiss on next outside click / escape
        const dismiss = (ev2) => {
            if (ctxMenuEl && (!ev2 || !ctxMenuEl.contains?.(ev2.target))) {
                hideContextMenu();
            }
        };
        const dismissKey = (ev2) => { if (ev2.key === "Escape")
            hideContextMenu(); };
        if (doc.addEventListener) {
            // Slight delay so the same click that opened the menu doesn't close it.
            setTimeout(() => {
                doc.addEventListener("click", dismiss, { once: true });
                doc.addEventListener("keydown", dismissKey, { once: true });
            }, 0);
        }
    }
    function hideContextMenu() {
        if (ctxMenuEl && ctxMenuEl.parentNode) {
            try {
                ctxMenuEl.parentNode.removeChild(ctxMenuEl);
            }
            catch (_) { /* element already detached during teardown; harmless */ }
        }
        ctxMenuEl = null;
    }
    const gesture = new InsertGesture({
        name: "insert-flow",
        target: overlay,
    });
    overlay.addEventListener("contextmenu", onContextMenu);
    return {
        destroy() {
            overlay.removeEventListener("contextmenu", onContextMenu);
            gesture.destroy();
            hideContextMenu();
        },
        // Test surface
        _commitInsert: commitInsert,
        _showContextMenu: showContextMenu,
    };
}
// ─── Save-block predicate ─────────────────────────────────────────────────────
/**
 * Build the save-block message shown to the user when they click Save while
 * any UNDEFINED elements exist.
 */
export function saveBlockState(store) {
    if (!store)
        return { blocked: false, count: 0, message: "" };
    const n = store.size();
    if (n === 0)
        return { blocked: false, count: 0, message: "" };
    return {
        blocked: true,
        count: n,
        message: `${n} ${n === 1 ? "element needs" : "elements need"} a type before saving.`,
    };
}
// ─── Type resolution ──────────────────────────────────────────────────────────
/**
 * Resolve a pending UNDEFINED insertion by picking a role. Removes the
 * pending entry from the store and returns the IR-side AddChild op spec
 * (caller dispatches via `restructurer.MutateKids(parent).AddChild(role)`).
 */
export function resolvePendingType(store, editorId, role) {
    const entry = store?.get(editorId);
    if (!entry)
        return null;
    const op = {
        kind: "add-child",
        parentStructId: entry.anchorStructId ?? null,
        role: String(role),
        bbox: entry.bbox,
        pdfBbox: entry.pdfBbox ?? null,
        page: entry.page,
    };
    store.remove(editorId);
    return op;
}
